import _ from "lodash";
import { router } from "@inertiajs/react";

export const navigateBackAndReload = () => {
  window.addEventListener(
    "popstate",
    (event) => {
      event.stopImmediatePropagation();
      router.visit(window.location.href, {
        replace: true,
        preserveScroll: true,
        preserveState: false,
      });
    },
    { once: true },
  );
  window.history.back();
};

// If this returns true (i.e. the user clicked a link on the sidebar/bottom navigation, or the tab/app opened directly to this page),
// then we will show the top and bottom navbars on the Calendar page
// If it returns false (e.g. the user clicked a link on the Clients page, Conversations page, Connect page, or anywhere else),
// then we will hide navigation and show a Back button on the Calendar page
export const getReferredFromNav = () => {
  return (
    // We were referred from sidebar/bottom navigation
    new URLSearchParams(window.location.search).get("from_nav") === "true" ||
    getReferredFromSignIn()
  );
};

export const getReferredFromSignIn = () => {
  return (
    // There is no known referrer, which means the user opened this tab from a bookmark,
    // or navigated directly, or the mobile app has just launched on this page
    // (In this case, we probably want the user to see the navbar in their Calendar page)
    (!window?.sessionStorage?.getItem("inertia-referrer-href") &&
      (!document?.referrer || document.referrer.indexOf("sign_in") !== -1)) ||
    // Fallback
    false
  );
};

export const clearNavigationReferrer = () => {
  window?.sessionStorage?.removeItem("inertia-referrer-href");
};

export const highestZIndexOfElementType = (elements) => {
  const highestZIndexElement = _.maxBy(
    Array.prototype.slice.call(elements),
    (e) => Number(e.style.zIndex),
  );
  return Number(highestZIndexElement.style.zIndex);
};

export const isMacPlatform = /mac/i.test(navigator.platform);

export const isSafari = /^((?!chrome|android).)*safari/i.test(
  navigator.userAgent,
);

export const youtubeRegexMatcher = new RegExp(
  /https?:\/\/(?:(?:www|m)\.)?youtube\.com(\/watch)?\?v=([\w\-_]*)(?:(?![.?!;](?:\s|$))[-a-zA-Z0-9@:;%._\+~#=&\?\/])*/gi,
); // will match https://www.youtube.com/watch?v=u5643U3qS8g plus additional URL parameters
export const shortYoutubeRegexMatcher = new RegExp(
  /https?:\/\/(?:(?:www|m)\.)?(?:youtu\.be|youtube\.com\/shorts)\/([^"&?\/ ]{11})(?:\?(?:(?![.?!;](?:\s|$))[-a-zA-Z0-9@:;%._\+~#=&\?\/])*)?/gi,
); // will match https://youtu.be/u5643U3qS8g or https://youtube.com/shorts/u5643U3qS8g

export const vimeoRegexMatcher = new RegExp(
  /https?:\/\/(?:www\.)?vimeo\.com\/(\d+(?:\/[\da-z]+)?)(?:[\?#](?:(?![.?!;\/](?:\s|$))[-a-zA-Z0-9@:;%._\+~#=&\?])*)?/gi,
);
export const vimeoPlayerRegexMatcher = new RegExp(
  /https?:\/\/(?:www\.)?(?:player\.)vimeo\.com\/(?:video\/)(\d+(?:\/[\da-z]+)?)(?:[\?#](?:(?![.?!;\/](?:\s|$))[-a-zA-Z0-9@:;%._\+~#=&\?])*)?/gi,
);

// Matches Instagram post, reel, and IGTV URLs
// Formats: instagram.com/p/ABC123/, instagram.com/reel/ABC123/, instagram.com/tv/ABC123/
export const instagramRegexMatcher = new RegExp(
  /https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel|tv)\/([\w-]+)\/?(?:\?[^\s]*)?/gi,
);

export const stringToFile = (uri, filename) => {
  let resolvedUrl = uri;
  let isCrossOrigin = false;
  let hasAttachmentDisposition = false;

  try {
    const parsedUrl = new URL(uri, window.location.href);
    resolvedUrl = parsedUrl.toString();
    isCrossOrigin = parsedUrl.origin !== window.location.origin;
    hasAttachmentDisposition = hasAttachmentDispositionParam(parsedUrl);
  } catch {
    resolvedUrl = uri;
  }

  // For cross-origin URLs with attachment disposition, fetch as blob so the
  // browser triggers a real download without opening a new tab or navigating
  // away. Fall back to _blank anchor if fetch fails (e.g. CORS restriction).
  if (isCrossOrigin && hasAttachmentDisposition) {
    const resolvedFilename =
      filename || extractFilenameFromDisposition(resolvedUrl);
    fetchAndDownload(resolvedUrl, resolvedFilename).catch(() => {
      triggerAnchorDownload(resolvedUrl, resolvedFilename, { newTab: true });
    });
    return;
  }

  // Cross-origin without attachment disposition: open in a new tab so we
  // never navigate away from the app (browsers ignore `download` cross-origin).
  triggerAnchorDownload(resolvedUrl, filename, { newTab: isCrossOrigin });
};

const fetchAndDownload = async (url, filename) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  try {
    triggerAnchorDownload(blobUrl, filename, { newTab: false });
  } finally {
    // Delay revoke so the browser has time to start the save dialog
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  }
};

const triggerAnchorDownload = (href, filename, { newTab }) => {
  const link = document.createElement("a");

  if (filename) {
    link.download = filename;
  } else {
    link.setAttribute("download", "");
  }

  link.href = href;

  if (newTab) {
    link.target = "_blank";
    link.rel = "noopener noreferrer";
  }

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const extractFilenameFromDisposition = (url) => {
  try {
    const parsedUrl = new URL(url);
    const disposition = parsedUrl.searchParams.get(
      "response-content-disposition",
    );
    if (!disposition) return undefined;
    const match = disposition.match(/filename[*]?=["']?([^"';]+)/i);
    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
};

const hasAttachmentDispositionParam = (parsedUrl) => {
  const disposition = parsedUrl.searchParams.get(
    "response-content-disposition",
  );
  if (!disposition) {
    return false;
  }

  return disposition.toLowerCase().includes("attachment");
};

export const isValidEmail = (email = "") => {
  let re =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return re.test(email);
};
